import { Accordion, Alert, Button, ButtonVariant, Text } from "@merge-api/merge-javascript-shared";
import { CircleCheck, RefreshCw, X } from "lucide-react";
import { palette } from "../../../../../styles/theme/colors";
import { IndividualTestExecution, TestExecutionStatus } from "../types";
import { usePublishModuleContext } from "../../context/PublishModuleContext";
import useKickOffIndividualAutomatedTest from "../hooks/useKickOffIndividualAutomatedTest";
import { GRAFANA_DEPLOYMENT_LOGS_URL_LAST_30_MINUTES } from "../../constants";

interface Props {
  testName: string;
  testExecution: IndividualTestExecution;
}

const getStatusIcon = (status: TestExecutionStatus): JSX.Element => {
  switch (status) {
    case TestExecutionStatus.PENDING:
    case TestExecutionStatus.IN_PROGRESS:
      return <RefreshCw className="rotating" color={palette.blue} size={16} />;
    case TestExecutionStatus.SUCCEEDED:
      return <CircleCheck size={16} className="text-teal-70" />;
    case TestExecutionStatus.FAILED:
    case TestExecutionStatus.ABORTED:
      return <X size={16} className="text-red-70" />;
    default:
      return <CircleCheck size={16} className="text-gray-40" />;
  }
};

const formatStartTime = (startTime: string | null): string | null => {
  if (!startTime) return null;
  try {
    const date = new Date(startTime);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

const formatExecutionTime = (timeInSeconds: number | null): string | null => {
  if (timeInSeconds === null || timeInSeconds === undefined) return null;

  if (timeInSeconds < 1) {
    return `${(timeInSeconds * 1000).toFixed(0)}ms`;
  } else if (timeInSeconds < 60) {
    return `${timeInSeconds.toFixed(2)}s`;
  } else {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = (timeInSeconds % 60).toFixed(0);
    return `${minutes}m ${seconds}s`;
  }
};

const getStatusMessage = (
  isPending: boolean,
  isInProgress: boolean,
  formattedStartTime: string | null
): string | null => {
  if (isPending) {
    if (formattedStartTime) {
      return `This test was queued at ${formattedStartTime} and is waiting to be picked up by a worker`;
    }
    return `This test has been queued and is waiting to be picked up by a worker`;
  }
  if (isInProgress && formattedStartTime) {
    return `This test started at ${formattedStartTime} and is currently in progress`;
  }
  return null;
};

const IndividualTestExecutionCard = ({ testName, testExecution }: Props) => {
  // hooks
  const {
    integrationID,
    automatedTestIntegrationVersionId,
    triggerAutomatedTestPoll,
    updateAutomatedTestExecution,
  } = usePublishModuleContext();

  const { kickOffIndividualTest, isKickingOff } = useKickOffIndividualAutomatedTest({
    integrationID,
    integrationVersionId: automatedTestIntegrationVersionId,
    onSuccess: (execution) => {
      if (updateAutomatedTestExecution) {
        updateAutomatedTestExecution(execution);
      }
      if (triggerAutomatedTestPoll) {
        triggerAutomatedTestPoll();
      }
    },
  });

  // derived state
  const validationResults = testExecution.validation_results || {};
  const hasValidationResults = Object.keys(validationResults).length > 0;

  const isPending = testExecution.status === TestExecutionStatus.PENDING;
  const isInProgress = testExecution.status === TestExecutionStatus.IN_PROGRESS;
  const isSuccessful = testExecution.status === TestExecutionStatus.SUCCEEDED;
  const isFailed =
    testExecution.status === TestExecutionStatus.FAILED ||
    testExecution.status === TestExecutionStatus.ABORTED;

  const formattedStartTime = formatStartTime(testExecution.start_time);
  const statusMessage = getStatusMessage(isPending, isInProgress, formattedStartTime);
  const executionTime = formatExecutionTime(testExecution.total_execution_time);
  const statusIcon = getStatusIcon(testExecution.status);

  // event handlers
  const handleRerun = (e: React.MouseEvent) => {
    e.stopPropagation();
    kickOffIndividualTest(testName);
  };

  // render

  return (
    <Accordion
      chevronSize={12}
      chevronOrientation="down"
      title={
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex flex-row items-center space-x-3">
            {statusIcon}
            <Text variant="md" className="font-semibold">
              {testName}
            </Text>
          </div>
          <Button
            size="sm"
            variant={ButtonVariant.TertiaryWhite}
            onClick={handleRerun}
            disabled={isKickingOff}
            leftIcon={isKickingOff ? <RefreshCw className="rotating" size={12} /> : undefined}
            className="mr-3"
          >
            Re-run
          </Button>
        </div>
      }
      titleClassName="p-3"
      defaultExpanded={false}
      variant="outline"
      className="mb-0"
    >
      <div className="px-3">
        <hr className="text-gray-40 m-0" />
        <div className="py-3 space-y-3">
          {/* Status Message for Pending/In-Progress Tests */}
          {statusMessage && (
            <div>
              <Alert color="blue">
                <Text variant="sm">
                  {statusMessage}. Click{" "}
                  <a
                    href={GRAFANA_DEPLOYMENT_LOGS_URL_LAST_30_MINUTES}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-60 hover:underline font-semibold"
                  >
                    here
                  </a>{" "}
                  to view the logs from this execution
                </Text>
              </Alert>
            </div>
          )}

          {/* Success Message */}
          {isSuccessful && (
            <div>
              <Alert color="green">
                <Text variant="sm">
                  Test suite completed successfully
                  {executionTime && ` in ${executionTime}`}
                </Text>
              </Alert>
            </div>
          )}

          {/* Failure Message */}
          {isFailed && !testExecution.exception_message && (
            <div>
              <Alert showWarningIcon color="red">
                <Text variant="sm">
                  Test suite failed
                  {executionTime && ` after ${executionTime}`}
                </Text>
              </Alert>
            </div>
          )}

          {/* Validation Results */}
          {hasValidationResults && (
            <div>
              <Text variant="sm" className="font-semibold text-gray-60 mb-1">
                Validation results
              </Text>
              <div className="rounded p-3 space-y-1">
                {Object.entries(validationResults).map(([key, passed]) => (
                  <div key={key} className="flex items-center space-x-2">
                    {passed ? (
                      <CircleCheck size={14} className="text-teal-70" />
                    ) : (
                      <X size={14} className="text-red-70" />
                    )}
                    <Text variant="sm" className="text-gray-80">
                      {key}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exception Message */}
          {testExecution.exception_message && (
            <Alert showWarningIcon color="red">
              <Text variant="sm">{testExecution.exception_message}</Text>
            </Alert>
          )}
        </div>
      </div>
    </Accordion>
  );
};

export default IndividualTestExecutionCard;
